import { useQueryClient } from '@tanstack/react-query';
import {
  createOrUpdateSeasonalRule,
  CreateOrUpdateSeasonalRuleArgs,
} from 'components/domain/property/seasonality/createOrUpdateSeasonalRule';
import { SEASONAL_RULES_BASE_QUERY_KEY } from 'components/domain/property/seasonality/seasonalRulesQuery';
import useAppMutation from 'hooks/useAppMutation';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

const SEASONAL_RULE_MUTATION_KEY = 'create-or-update-seasonal-rule';

const useSeasonalRuleMutation = () => {
  const { notifySuccess, notifyError } = useNotify();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutateAsync: saveSeasonalRule, isLoading: isSubmitting } =
    useAppMutation(
      [SEASONAL_RULE_MUTATION_KEY],
      ({
        uid,
        payload,
      }: CreateOrUpdateSeasonalRuleArgs & {
        successCallback?: () => void;
      }) => createOrUpdateSeasonalRule({ uid, payload }),
      {
        onSuccess: (response, { uid, successCallback }) => {
          queryClient.invalidateQueries({
            queryKey: [SEASONAL_RULES_BASE_QUERY_KEY],
          });

          const isEdit = !!uid;
          const propertyErrorsCount = response.propertyErrors?.length ?? 0;

          if (propertyErrorsCount > 0) {
            notifyError(
              t('pageProperty.seasonality.modal.overlapWarning', {
                count: propertyErrorsCount,
              }),
            );
          } else {
            notifySuccess(
              t(
                isEdit
                  ? 'pageProperty.seasonality.modal.updateSuccess'
                  : 'pageProperty.seasonality.modal.createSuccess',
              ),
            );
          }

          successCallback?.();
        },
        onError: (error: BaseErrorResponse, { uid }) => {
          const apiErrorCode = error?.response?.data?.apiErrorCode;
          const apiErrorMessage = error?.response?.data?.apiErrorMessage;
          const isEdit = !!uid;

          if (apiErrorCode || apiErrorMessage) {
            notifyError(
              getErrorMessage({
                apiErrorCode,
                apiErrorMessage,
                baseKey: 'pageProperty.apiErrorCodes.',
                t,
              }),
            );
            return;
          }

          notifyError(
            t(
              isEdit
                ? 'pageProperty.seasonality.modal.updateError'
                : 'pageProperty.seasonality.modal.createError',
            ),
          );
        },
      },
    );

  return { saveSeasonalRule, isSubmitting };
};

export default useSeasonalRuleMutation;
